import {Box, Button, Card, Container, Divider, Stack, TextField, Typography} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';

import {toast} from 'react-toastify';
import SubmitButton from 'src/components/buttons/SubmitButton';
import PhoneInput from 'src/components/inputs/PhoneInput';
import Picker from 'src/components/inputs/Picker';
import Row from 'src/components/ui/Row';
import FormikWrapper from 'src/components/wrappers/FormikWrapper';
import useFind from 'src/hooks/useFind';

import {useQuery} from 'react-query';
import AccessControlWrapper from 'src/components/AccessControlWrapper';
import CheckboxWrapper from 'src/components/inputs/CheckboxWrapper';
import Page from 'src/components/Page';
import InfoTip from 'src/components/ui/InfoTip';
import BasicRadioGroup from 'src/components/wrappers/BasicRadioGroup';
import {order_selected_date_feature_flag} from 'src/feature_flag_list';
import useSite from 'src/hooks/useSite';
import {
	addDiscountPermission,
	createOrderPermission,
	findOneOrderPermission,
} from 'src/permissionsList';
import {LoadingView} from 'src/routes';
import {createOrder, getCities, request} from 'src/utils/AdminApi';
import {getCartItemsTotalPrice, getCartItemsTotalQuantity} from 'src/utils/cartItemUtils';
import {getCity} from 'src/utils/cityUtils';
import {accountIsDemo} from 'src/utils/customSite';
import {isFeatureAllowed} from 'src/utils/featureUtils';
import {getPriceString, handleRequestError, isNullOrEmpty} from 'src/utils/helperMethods';
import {buildOrderItem, isCartItemValid} from 'src/utils/orderItemUtils';
import {orderStatusOptions} from 'src/utils/orderUtils';
import {canAccess} from 'src/utils/permissions';
import {getSiteCurrency} from 'src/utils/selectors';
import {CustomAutoComplete} from 'vetrina-ui';
import {getNumber} from '../../../utils/numberUtils';
import CartItemSelector from '../CartItemSelector';
import useOrderItemsSelection from '../NewAddOrderPage/useOrderItemsSelection';
import useDuplicateOrder from '../useDuplicateOrder';
import {createOrderSchema} from './order.schema';
import formatCreateOrderError from '../formatCreateOrderError';
import {getTestFlag} from 'src/getTestFlag';
import {useState} from 'react';
import {simpleItemsSchema} from '../simpleschema';
import {scrollToElement} from 'src/utils/dom';
import {validateYup} from 'src/utils/yup';
import FileUploadField from 'src/components/inputs/ImageCropPicker.js/FileUploadField';
import Dev from 'src/components/Dev';
import GalleryPicker from 'src/components/GalleryPicker';

export default function CreateOrder() {
	const navigate = useNavigate();
	const {data: countries = []} = useQuery('countries', () => request('/countries'));
	const {data: customers = []} = useQuery('customers', () => request('/customers'));
	const [cities] = useFind(getCities);
	// these variables for inputs coming from dublicate button in order details
	const location = useLocation();
	// we will get order from duplicate button , or product from barcode page
	const {order} = location.state ?? {};
	const tenant = useSite();
	const {
		handleItemQuantityChange,
		checkAddedItemExists,
		handleAddItem,
		handleRemoveItem,
		handleRemoveItemByProductId,
		addedProducts,
		setAddedProducts,
		resetSelections,
		handleItemChange,
	} = useOrderItemsSelection([]);
	const {isLoading: isLoadingDuplicatedItems} = useDuplicateOrder({
		order,
		setOrderItems: setAddedProducts,
	});

	const [simpleProducts, setSimpleProducts] = useState([]);

	const ite = addedProducts.length > 0 || simpleProducts.length > 0;

	const handleSubmit = async (values) => {
		if (!ite) {
			return toast.error('يجب اختيار منتج واحد علي الاقل');
		}

		if (!isCartItemValid(addedProducts)) return toast.error('يرجى تعبئة جميع الحقول');
		if (simpleProducts.length > 0) {
			const validation = validateYup(simpleItemsSchema, simpleProducts);
			if (!validation.isValid) {
				console.log(validation.errors);
				scrollToElement('simple-products');
				return toast.error(`يرجى ادخال المنتجات بشكل صحيح`);
			}
		}

		const {
			down_payment,
			city,
			district,
			delivery_method,
			status,
			name,
			email,
			phone,
			address,
			delivery_price,
			note,
			private_note,
			discount,
			selected_date,
			// custom fields
			phone2 = '',
			country,
			is_cashier = false,
			payment_received = false,
		} = values;
		// const fullAddress = isNullOrEmpty(city) ? address : `${city} ${address}`;

		const isPickup = delivery_method === 'pickup';
		const cart = [...addedProducts.map(buildOrderItem), ...simpleProducts.map(buildOrderItem)];

		const customersInfo = getCustomersInfo(values);

		try {
			const res = await createOrder({
				address: !isPickup ? address : '',
				...customersInfo,
				payment: {
					gateway: values.gateway,
				},
				cart,
				delivery_price,
				note,
				private_note,
				status,
				down_payment: getNumber(down_payment),
				delivery_info: {
					type: delivery_method || 'pickup',
					city: !isPickup ? city : '',
					district: !isPickup ? district : '',
					country: country ?? 'LY',
				},
				custom_fields: {
					phone2,
				},
				is_cashier, //is_cashier,
				payment_received,
				discount,
				selected_date,
				attachments: values.attachments?.map((a) => a.id) || [],
			});
			toast.success('تم انشاء الطلب بنجاح');
			if (canAccess(findOneOrderPermission)) navigate('/orders/' + res.uuid);
			else {
				navigate(0);
			}
		} catch (error) {
			toast.error(formatCreateOrderError(error).errorMessage);
		}
	};

	if (isLoadingDuplicatedItems) return <LoadingView />;

	return (
		<Page title="انشاء طلب">
			<AccessControlWrapper controllerAction={createOrderPermission}>
				<Container maxWidth="md">
					<FormikWrapper
						values={{
							name: order?.customer?.name ?? '',
							status: 'review',
							email: order?.customer?.email ?? '',
							phone: order?.customer?.phone ?? '',
							address: order?.address ?? '',
							note: order?.note ?? '',
							// we need to set datetime format "yyyy-MM-dd'T'HH:mm" without .000Z
							// selected_date: order?.selected_date ? getUtcDateTimeFromISO(order.selected_date) : '',
							discount: order?.discount,
							// delivery_price: order?.delivery_price ?? '',
							country: 'LY',
							delivery_method: order?.delivery_info?.type ?? 'pickup',
							city: order?.delivery_info?.city ?? '',
							district: order?.delivery_info?.district ?? '',
							payment_received: false,
							// this is beta feature, we will enable it for some users
							is_cashier: tenant.config?.features?.always_cashier ?? false,
							gateway: 'cash',
							// extra custom fields
							phone2: '',
							attachments: null,
							customer: null,
						}}
						schema={createOrderSchema}
						handleSubmit={handleSubmit}
					>
						{({values, setValues, setFieldValue, isSubmitting, getInputProps}) => {
							const selectedCity = getCity(cities, values.city);
							const cityHasDistricts = !isNullOrEmpty(selectedCity?.districts);
							return (
								<Stack spacing={2}>
									<Typography variant="h3">اضافة طلب</Typography>
									{getTestFlag() && (
										<Button
											type="button"
											onClick={() => {
												setValues({
													...values,
													delivery_method: 'delivery',
													country: 'LY',
													city: 'طرابلس',
													district: 'جنزور',
													// customer info
													name: 'اسم الزبون',
													email: 'customer@example.com',
													phone: '0924474423',
												});
											}}
										>
											auto fill delivery
										</Button>
									)}
									<Card sx={{p: 2}}>
										<CartItemSelector
											title="اختر المنتجات"
											handleItemQuantityChange={handleItemQuantityChange}
											checkAddedItemExists={checkAddedItemExists}
											handleAddItem={handleAddItem}
											handleRemoveItem={handleRemoveItem}
											handleRemoveItemByProductId={handleRemoveItemByProductId}
											handleItemChange={handleItemChange}
											addedProducts={addedProducts}
											resetSelections={resetSelections}
											// we don't want to show products popup if we are duplicating an order
											// defaultVisibility={!order && addedProducts.length === 0}
											defaultVisibility={false}
											showBranch
											simpleProducts={simpleProducts}
											setSimpleProducts={setSimpleProducts}
										/>
										{ite && (
											<Box bgcolor={'#F2F2F2'} mt={1}>
												<Row label="عدد القطع" value={getCartItemsTotalQuantity(addedProducts)} />
												<Divider />
												<Row
													label="مجموع المشتريات"
													value={getPriceString(
														getCartItemsTotalPrice([...addedProducts, ...simpleProducts]),
														getSiteCurrency(),
													)}
												/>
											</Box>
										)}
									</Card>

									{ite && (
										<>
											<Card sx={{p: 2}}>
												<Stack spacing={2}>
													<Typography variant="h6">معلومات الزبون</Typography>

													<CustomAutoComplete
														options={customers || []}
														label="الزبون"
														{...getInputProps('customer')}
														onChange={(e, opt) => {
															setFieldValue('customer', opt ?? null);
															setFieldValue('name', opt?.name ?? '');
															setFieldValue('phone', opt?.phone ?? '');
															setFieldValue('email', opt?.email ?? '');
															// address
															setFieldValue('country', opt?.country ?? 'LY');
															setFieldValue('city', opt?.city ?? '');
															setFieldValue('district', opt?.district ?? '');
															setFieldValue('address', opt?.address ?? '');

														}}
														onInputChange={(event, newInputValue) => {
															setFieldValue('name', newInputValue);
															setFieldValue('phone', '');
															setFieldValue('email', '');
															setFieldValue('country', 'LY');
															setFieldValue('city', '');
															setFieldValue('district', '');
															setFieldValue('address', '');
														}}
														filterOptions={(options, {inputValue}) => {
															if (!inputValue) return options;
															const searchTerm = inputValue.toLowerCase();
															return options.filter((option) => {
																const name = option.name?.toLowerCase() || '';
																const phone = option.phone?.toLowerCase() || '';
																const email = option.email?.toLowerCase() || '';
																return (
																	name.includes(searchTerm) ||
																	phone.includes(searchTerm) ||
																	email.includes(searchTerm)
																);
															});
														}}
														labelKey="name"
														freeSolo
														value={values.customer}
														helperText="يمكن البحث  عن زبون سابق باستخدام الاسم، رقم الهاتف او البريد الالكتروني"
													/>

													{/* <TextField fullWidth type="text" label="اسم الزبون" {...getInputProps('name')} /> */}
													<PhoneInput
														label="رقم الهاتف"
														placeholder="رقم الهاتف"
														{...getInputProps('phone')}
														fullWidth
														type="tel"
													/>
													<PhoneInput
														label="رقم هاتف - احتياطي"
														placeholder="رقم هاتف - احتياطي"
														{...getInputProps('phone2')}
														fullWidth
														type="tel"
													/>
													<TextField
														fullWidth
														type="text"
														label="البريد الإلكتروني"
														{...getInputProps('email')}
													/>
												</Stack>
											</Card>

											<Card sx={{p: 2}}>
												<Stack spacing={2}>
													<Typography variant="h6">التوصيل والاستلام</Typography>
													<Picker
														options={[
															{id: 'pickup', label: 'استلام شخصي'},
															{id: 'delivery', label: 'توصيل'},
														]}
														label="طريقة الاستلام"
														labelKey="label"
														valueKey="id"
														defaultValue="pickup"
														{...getInputProps('delivery_method')}
													/>

													{values.delivery_method === 'delivery' && (
														<>
															{accountIsDemo() && (
																<CustomAutoComplete
																	options={countries}
																	label="الدولة"
																	{...getInputProps('country')}
																	onChange={(e, opt) => setFieldValue('country', opt?.iso_code)}
																	labelKey="name"
																	valueKey="iso_code"
																/>
															)}

															<CustomAutoComplete
																options={values.country == 'LY' ? cities.map((c) => c.name) : []}
																label="المدينة"
																{...getInputProps('city')}
																onChange={(e, opt) => {
																	setFieldValue('city', opt ?? '');
																	setFieldValue('district', '');
																}}
																onInputChange={(event, newInputValue) => {
																	if (values.city === newInputValue) return;
																	setFieldValue('city', newInputValue ?? '');
																}}
																freeSolo={values.country != 'LY'}
																value={values.city}
															/>

															{cityHasDistricts && (
																<CustomAutoComplete
																	{...getInputProps('district')}
																	options={selectedCity?.districts?.map((d) => d.name) ?? []}
																	label="المنطقة"
																	onChange={(e, opt) => {
																		if (values.district === opt) return;
																		setFieldValue('district', opt ?? '');
																	}}
																	value={values.district}
																/>
															)}
															<TextField
																label="العنوان"
																placeholder="العنوان"
																{...getInputProps('address')}
																fullWidth
																type="text"
															/>
															{accountIsDemo() && (
																<TextField
																	label="سعر التوصيل"
																	placeholder="سعر التوصيل"
																	{...getInputProps('delivery_price')}
																	fullWidth
																	type="number"
																	helperText="only for development"
																/>
															)}
														</>
													)}
												</Stack>
												{isFeatureAllowed(order_selected_date_feature_flag) && (
													<TextField
														{...getInputProps('selected_date')}
														sx={{
															mt: 2,
														}}
														label={'موعد التسليم'}
														InputLabelProps={{
															shrink: true,
														}}
														fullWidth
														type="datetime-local"
													/>
												)}
											</Card>
											<Card sx={{p: 2}}>
												<Stack spacing={3}>
													<Typography variant="h6">معلومات اضافية</Typography>

													{/* 		<TextField
												label="االبريد الالكتروني"
												placeholder="البريد الالكتروني"
												{...getInputProps('email')}
												fullWidth
												type="email"
											/> */}

													<Picker
														options={orderStatusOptions.filter(
															(o) => o.key != 'cancelled' && o.key != 'returned',
														)}
														label="حالة الطلب"
														labelKey="label"
														valueKey="key"
														{...getInputProps('status')}
													/>

													<TextField
														label="ملاحظات"
														placeholder="ملاحظات اضافية عن الطلب"
														{...getInputProps('note')}
														fullWidth
														minRows={3}
														multiline
														type="text"
													/>

													<TextField
														label="ملاحظات خاصة - لاتظهر للزبون"
														placeholder="ملاحظات خاصة"
														{...getInputProps('private_note')}
														fullWidth
														minRows={3}
														multiline
														type="text"
													/>

													<TextField
														fullWidth
														type="text"
														inputMode="numeric"
														pattern="\d*"
														InputProps={{min: 0}}
														label="عربون"
														{...getInputProps('down_payment')}
													/>

													{canAccess(addDiscountPermission) && (
														<TextField
															fullWidth
															type="text"
															inputMode="numeric"
															pattern="\d*"
															InputProps={{min: 0}}
															label="تخفيض"
															{...getInputProps('discount')}
														/>
													)}

													<BasicRadioGroup
														value={values.gateway}
														onChange={(e) => {
															setFieldValue('gateway', e.target.value);
														}}
														label="طريقة الدفع"
														options={[
															{label: 'كاش', value: 'cash'},
															{label: 'دفع الكتروني', value: 't_lync'},
															{label: 'حوالة بنكية', value: 'bank_transfer'},
														]}
													/>

													{isFeatureAllowed('order_attachments') && (
														<GalleryPicker
															value={values.attachments}
															onChange={(newValue) => {
																setFieldValue('attachments', newValue);
															}}
															showDeleteButton
															allowMultiple
															maxFiles={5}
															enableCrop
															label="المرفقات"
															placeholder="المرفقات"
														/>
													)}

													<Stack spacing={1} direction="row" justifyContent="space-between">
														{accountIsDemo() && (
															<Stack direction="row">
																<CheckboxWrapper {...getInputProps('is_cashier')} label="كاشير؟" />
																<InfoTip
																	devOnly
																	title="هذا الخيار يطلعلنا احني بس، العسال حيتم اضافة الطلب ككشير ويمكن تفعيلها لاي متجر اخر من ادمن فترينا"
																/>
															</Stack>
														)}

														<CheckboxWrapper {...getInputProps('payment_received')} label="تم الدفع؟" />
													</Stack>

													<SubmitButton isSubmitting={isSubmitting}>انشاء</SubmitButton>

													<Dev title="debug" object={values} />
												</Stack>
											</Card>
										</>
									)}
								</Stack>
							);
						}}
					</FormikWrapper>
				</Container>
			</AccessControlWrapper>
		</Page>
	);
}

function getCustomersInfo(values) {
	return {
		email: values.email,
		phone: values.phone,
		name: values.name,
		user: values.customer?.id ?? null,
	};
}
